import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Divider } from '@mui/material';
import { sessionActions } from '../store';
import { nativePostMessage } from '../common/components/NativeInterface';

// Sign in with an account somebody has already created. Google settles who the person is; it does
// not settle whether they may use this system, so the server refuses an address nobody provisioned
// rather than creating a user from whoever arrives. The button renders only where the deployment
// has been given its own OAuth client, so nothing appears — and no Google script loads — otherwise.
const SCRIPT = 'https://accounts.google.com/gsi/client';

const loadGoogle = () => new Promise((resolve, reject) => {
  if (window.google?.accounts?.id) { resolve(window.google); return; }
  const existing = document.querySelector(`script[src="${SCRIPT}"]`);
  const done = () => (window.google?.accounts?.id ? resolve(window.google) : reject(new Error('unavailable')));
  if (existing) { existing.addEventListener('load', done); existing.addEventListener('error', () => reject(new Error('unavailable'))); return; }
  const script = document.createElement('script');
  script.src = SCRIPT; script.async = true; script.defer = true;
  script.addEventListener('load', done);
  script.addEventListener('error', () => reject(new Error('unavailable')));
  document.head.appendChild(script);
});

const GoogleSignIn = () => {
  const clientId = useSelector((state) => state.session.server?.googleClientId);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const container = useRef(null);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!clientId || !container.current) return undefined;
    let cancelled = false;
    const signIn = async (credential) => {
      setError('');
      try {
        const response = await fetch('/api/session/google', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ credential }),
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) { setError(body.message || 'Google sign-in was refused.'); return; }
        dispatch(sessionActions.updateUser(body));
        nativePostMessage('login');
        navigate('/');
      } catch {
        setError('Google sign-in could not reach the server.');
      }
    };
    loadGoogle().then((google) => {
      if (cancelled || !container.current) return;
      google.accounts.id.initialize({ client_id: clientId, callback: (response) => signIn(response.credential), ux_mode: 'popup' });
      google.accounts.id.renderButton(container.current, { theme: 'outline', size: 'large', text: 'signin_with', width: 320 });
      setReady(true);
    }).catch(() => { if (!cancelled) setError('Google sign-in is unavailable right now. Use your email and password.'); });
    return () => { cancelled = true; };
  }, [clientId, dispatch, navigate]);
  if (!clientId) return null;
  return (
    <>
      <Divider>or</Divider>
      <Box ref={container} sx={{ display: 'flex', justifyContent: 'center', minHeight: ready ? 'auto' : 0 }} />
      {error && <Alert severity="error">{error}</Alert>}
    </>
  );
};

export default GoogleSignIn;
